import { Action } from 'redux';
import { VendorSearchViewModel, TradeViewModel, PriceAgreementViewModel } from '@models/Vendor';
import { Dispatch } from 'react-redux';
import { vendorService } from '@services/VendorService';

import { toast } from 'react-toastify';
import { ToastComponent, ToastType } from '@common/components';
import { ToastOptions } from '@common/components/toast/toast';
import * as React from 'react';
import { PageViewModel, OptionsViewModel, AppDispatcher } from '@models/common.models';
import { AppState } from '../../..';
import { TradeOptionsViewModel } from '@models/ServiceWorkOrder';
import { VendorState } from 'src/vendor/vendor.reducer';
import { requestAction } from '@common/components/common.actions';

export enum VendorDashboardActionType {
  SearchVendorsInit = 'SearchVendorsInit',
  SearchVendorsSuccess = 'SearchVendorsSuccess',
  SearchVendorsFailure = 'SearchVendorsFailure',

  SearchPriceAgreementsInit = 'SearchPriceAgreementsInit',
  SearchPriceAgreementsSuccess = 'SearchPriceAgreementsSuccess',
  SearchPriceAgreementsFailure = 'SearchPriceAgreementsFailure',

  GetTradesFailure = 'GetTradesFailure',
  GetTradesStart = 'GetTradesStart',
  GetTradesSuccess = 'GetTradesSuccess',

  SaveVendorTradesFailure = 'SaveVendorTradesFailure',
  SaveVendorTradesStart = 'SaveVendorTradesStart',
  SaveVendorTradesSuccess = 'SaveVendorTradesSuccess',

  SavePriceAgreementFailure = 'SavePriceAgreementFailure',
  SavePriceAgreementStart = 'SavePriceAgreementStart',
  SavePriceAgreementSuccess = 'SavePriceAgreementSuccess',

  ClearVendorSearch = 'ClearVendorTrades',
  ClearPriceAgreementsSearch = 'ClearPriceAgreementsSearch'
}

export interface VendorDashboardAction extends Action {
  readonly type: VendorDashboardActionType;
}
/***** Get Trades *****/
export interface GetTradesFailureAction extends VendorDashboardAction {}
export interface GetTradesStartAction extends VendorDashboardAction {}
export interface GetTradesSuccessAction extends VendorDashboardAction {
  readonly results: TradeOptionsViewModel[];
}

/***** Save Vendor VendorTrades *****/
export interface SaveVendorTradesFailureAction extends VendorDashboardAction {}
export interface SaveVendorTradesStartAction extends VendorDashboardAction {}
export interface SaveVendorTradesSuccessAction extends VendorDashboardAction {
  readonly results: TradeViewModel[];
}
export interface SavePriceAgreementStartAction extends Action {
  readonly type: VendorDashboardActionType.SavePriceAgreementStart;
  readonly message: string;
}

export interface SavePriceAgreementSuccessAction extends Action {
  readonly type: VendorDashboardActionType.SavePriceAgreementSuccess;
  readonly message: string;
}

export interface SavePriceAgreementFailureAction extends Action {
  readonly type: VendorDashboardActionType.SavePriceAgreementFailure;
  readonly errorMessage: string;
}

export interface ClearVendorSearchAction extends VendorDashboardAction {}
export interface ClearPriceAgreementsSearchAction extends VendorDashboardAction {}

/********** Search **********/
export interface SearchVendorsInitAction extends Action {
  readonly type: VendorDashboardActionType.SearchVendorsInit;
  readonly query: string | null;
  readonly vendorType?: OptionsViewModel;
  readonly vendorTrade?: TradeOptionsViewModel;
  readonly vendorStatus?: OptionsViewModel;
  readonly vendorGroup?: OptionsViewModel;
}

export interface SearchPriceAgreementsInitAction extends Action {
  readonly type: VendorDashboardActionType.SearchPriceAgreementsInit;
  readonly query: string | null;
  readonly paymentType?: string;
  readonly cviFilter?: OptionsViewModel;
}

export interface SearchVendorsSuccessAction extends Action {
  readonly type: VendorDashboardActionType.SearchVendorsSuccess;
  readonly pageNumber: number;
  readonly pageSize: number;
  readonly searchResult: PageViewModel<VendorSearchViewModel>;
}

export interface SearchPriceAgreementsSuccessAction extends Action {
  readonly type: VendorDashboardActionType.SearchPriceAgreementsSuccess;
  readonly pageNumber: number;
  readonly pageSize: number;
  readonly searchResult: PageViewModel<PriceAgreementViewModel>;
}

export interface SearchVendorsFailureAction extends Action {
  readonly type: VendorDashboardActionType.SearchVendorsFailure;
  readonly errorMessage: string;
}

export interface SearchPriceAgreementsFailureAction extends Action {
  readonly type: VendorDashboardActionType.SearchPriceAgreementsFailure;
  readonly errorMessage: string;
}

/***** Get Trades *****/
export function getTrades(): (dispatch: AppDispatcher) => Promise<GetTradesSuccessAction | undefined> {
  return async (dispatch: Dispatch<VendorState>) => {
    return dispatch(
      requestAction(() => vendorService.getTrades(), getTradesSuccess, {
        overrideFailMessage: 'Unable to get Vendor, try again later.'
      })
    );
  };
}

export function getTradesStart(): GetTradesStartAction {
  return {
    type: VendorDashboardActionType.GetTradesStart
  };
}

export function getTradesSuccess(results: TradeOptionsViewModel[]): GetTradesSuccessAction {
  return {
    type: VendorDashboardActionType.GetTradesSuccess,
    results: results
  };
}

export function getTradesFailure(error: string): GetTradesFailureAction {
  toast.error(<ToastComponent type={ToastType.Error} message={error.toString()} />, ToastOptions.Error);
  return {
    type: VendorDashboardActionType.GetTradesFailure
  };
}

export function clearVendorSearch(): ClearVendorSearchAction {
  return {
    type: VendorDashboardActionType.ClearVendorSearch
  };
}

export function clearPriceAgreementsSearch(): ClearPriceAgreementsSearchAction {
  return {
    type: VendorDashboardActionType.ClearPriceAgreementsSearch
  };
}

/***** Get Vendor Trades *****/
export function saveVendorTradesStart(): SaveVendorTradesStartAction {
  return {
    type: VendorDashboardActionType.SaveVendorTradesStart
  };
}

export function saveVendorTradesSuccess(results: TradeViewModel[]): SaveVendorTradesSuccessAction {
  return {
    type: VendorDashboardActionType.SaveVendorTradesSuccess,
    results: results
  };
}

export function saveVendorTradesFailure(error: string): SaveVendorTradesFailureAction {
  toast.error(<ToastComponent type={ToastType.Error} message={error.toString()} />, ToastOptions.Error);
  return {
    type: VendorDashboardActionType.SaveVendorTradesFailure
  };
}

/***********Search ********/
export function lookupVendor(value: string, id: number) {
  return vendorService.searchVendors(value, id);
}

export const searchPriceAgreements = (
  id: number,
  query: string | null,
  pageNumber: number,
  pageSize: number,
  filterBy: string,
  orderBy: string,
  orderDirection: string,
  filters?: OptionsViewModel
) => {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(searchPriceAgreementsInit(query, filterBy, filters));
    try {
      let searchResult: PageViewModel<PriceAgreementViewModel> = await vendorService.searchPriceAgreements(
        id,
        query,
        pageNumber,
        pageSize,
        filterBy,
        orderBy,
        orderDirection,
        filters
      );
      dispatch(searchPriceAgreementsSuccess(pageNumber, pageSize, searchResult));
    } catch (e) {
      dispatch(searchVendorsFailure(e.toString()));
    }
  };
};

export function savePriceAgreement(vendorId: number, agreement: PriceAgreementViewModel, callback: Function) {
  return async (dispatch: Dispatch<AppState>) => {
    try {
      dispatch(SavePriceAgreementStart());

      await vendorService.savePriceAgreement(vendorId, agreement).then(() => {
        dispatch(SavePriceAgreementSuccess());
        callback();
      });
    } catch (e) {
      dispatch(SavePriceAgreementFailure(e.message));
    }
  };
}

export function SavePriceAgreementStart(): SavePriceAgreementStartAction {
  return {
    type: VendorDashboardActionType.SavePriceAgreementStart,
    message: 'Saving Price Agreemend Start'
  };
}

export function SavePriceAgreementSuccess(): SavePriceAgreementSuccessAction {
  // TODO REFRESH AFTER
  toast.success(
    <ToastComponent type={ToastType.Success} message={`Price Agreement Saved Successfully`} />,
    ToastOptions.Success
  );

  return {
    type: VendorDashboardActionType.SavePriceAgreementSuccess,
    message: 'Price Agreement Saved Successfully'
  };
}

export function SavePriceAgreementFailure(errorMessage: string): SavePriceAgreementFailureAction {
  toast.error(<ToastComponent type={ToastType.Error} message={errorMessage} />, ToastOptions.Error);

  return {
    type: VendorDashboardActionType.SavePriceAgreementFailure,
    errorMessage: errorMessage
  };
}

export const searchVendors = (
  query: string | null,
  pageSize: number,
  pageNumber: number,
  orderBy: string,
  orderDirection: string,
  vendorTrade?: TradeOptionsViewModel,
  vendorType?: OptionsViewModel,
  vendorStatus?: OptionsViewModel,
  vendorGroup?: OptionsViewModel
) => {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(searchVendorsInit(query, vendorType, vendorTrade, vendorStatus, vendorGroup));
    try {
      let searchResult: PageViewModel<VendorSearchViewModel> = await vendorService.searchVendors(
        query,
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        vendorTrade ? vendorTrade.value : '',
        vendorType ? vendorType.value : '',
        vendorStatus ? vendorStatus.value : '',
        vendorGroup ? vendorGroup.value : ''
      );
      dispatch(searchVendorsSuccess(pageNumber, pageSize, searchResult));
    } catch (e) {
      dispatch(searchVendorsFailure(e.toString()));
    }
  };
};

export function searchVendorsInit(
  query: string | null,
  vendorType?: OptionsViewModel,
  vendorTrade?: TradeOptionsViewModel,
  vendorStatus?: OptionsViewModel,
  vendorGroup?: OptionsViewModel
): SearchVendorsInitAction {
  return {
    query: query,
    type: VendorDashboardActionType.SearchVendorsInit,
    vendorType: vendorType,
    vendorTrade: vendorTrade,
    vendorStatus: vendorStatus,
    vendorGroup: vendorGroup
  };
}

export function searchPriceAgreementsInit(
  query: string | null,
  paymentType?: string,
  cviFilter?: OptionsViewModel
): SearchPriceAgreementsInitAction {
  return {
    query: query,
    type: VendorDashboardActionType.SearchPriceAgreementsInit,
    paymentType: paymentType,
    cviFilter: cviFilter
  };
}

export function searchVendorsFailure(errorMessage: string): SearchVendorsFailureAction {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Network error, unable to search vendors. ' + errorMessage} />,
    ToastOptions.Error
  );

  return {
    errorMessage: errorMessage,
    type: VendorDashboardActionType.SearchVendorsFailure
  };
}

export function searchPriceAgreementsFailure(errorMessage: string): SearchPriceAgreementsFailureAction {
  toast.error(
    <ToastComponent
      type={ToastType.Error}
      message={'Network error, unable to search price agreements. ' + errorMessage}
    />,
    ToastOptions.Error
  );

  return {
    errorMessage: errorMessage,
    type: VendorDashboardActionType.SearchPriceAgreementsFailure
  };
}

export function searchVendorsSuccess(
  pageNumber: number,
  pageSize: number,
  result: PageViewModel<VendorSearchViewModel>
): SearchVendorsSuccessAction {
  return {
    pageNumber: pageNumber,
    pageSize: pageSize,
    searchResult: result,
    type: VendorDashboardActionType.SearchVendorsSuccess
  };
}

export function searchPriceAgreementsSuccess(
  pageNumber: number,
  pageSize: number,
  result: PageViewModel<PriceAgreementViewModel>
): SearchPriceAgreementsSuccessAction {
  return {
    pageNumber: pageNumber,
    pageSize: pageSize,
    searchResult: result,
    type: VendorDashboardActionType.SearchPriceAgreementsSuccess
  };
}



// WEBPACK FOOTER //
// ./src/vendor/_components/vendor-dashboard/vendor-dashboard.actions.tsx